import React, { useState, useEffect, useRef } from 'react';
import { Button } from 'react-bootstrap';
import DataTable from 'react-data-table-component';
import { BsMic, BsMicMute } from 'react-icons/bs';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import { apiPost, apiGet } from '../services/api';
import DeadlineHistoryModal from '../components/modals/DeadlineHistoryModal';
import AttachmentsModal from '../components/modals/AttachmentsModal';
import CommentsModal from '../components/modals/CommentsModal';
import { toast } from 'react-toastify';
import { BsCalendar, BsClockHistory, BsPaperclip, BsChatText } from 'react-icons/bs';

let usersArray = [];
let prioritiesArray = [];
let categoriesArray = [];
let groupsArray = [];
let statusArray = [];

const VoiceSearch = () => {
 
    const [allUsers, setAllUsers] = useState([]);
    const [taskCoordinators, setTaskCoordinators] = useState([]);
    const [priorities, setPriorities] = useState([]);
    const [categories, setCategories] = useState([]);
    const [groups, setGroups] = useState([]);
    const [statuses, setStatuses] = useState([]);
    const [tasks, setTasks] = useState([]);
    const [filteredTasks, setFilteredTasks] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [expandedRows, setExpandedRows] = useState({});
    const [voiceQuery, setVoiceQuery] = useState('');
    const [isListening, setIsListening] = useState(false);
    const [apiError, setApiError] = useState(null);
    const [showSearchWarning, setShowSearchWarning] = useState(false);
    const recognitionRef = useRef(null);
    const alertTimeoutRef = useRef(null);

  
    const [deadlineHistoryModal, setDeadlineHistoryModal] = useState({
        visible: false,
        loading: false,
        deadlines: [],
        currentTaskId: null
    });

    const [attachmentsModal, setAttachmentsModal] = useState({
        show: false,
        taskId: null,
        officerNames: '',
        attachments: [],
        onFileDelete: () => { }
    });

    const [commentsModal, setCommentsModal] = useState({
        show: false,
        taskId: null,
        officerNames: '',
        comments: [],
        existingComments: ''
    });

   
    const hasSearchParameters = (query) => {
        if (!query || query.trim() === '' || query === 'Listening...') {
            return false;
        }
        
        const lowerQuery = query.toLowerCase().trim();
        const searchKeywords = [
            'task id', 'priority', 'status', 'category', 
            'group', 'task coordinator', 'date', 'deadline', 'attachment'
        ];
        
        return searchKeywords.some(keyword => lowerQuery.includes(keyword));
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            return format(new Date(dateString), 'dd/MM/yyyy');
        } catch {
            return 'Invalid Date';
        }
    };

    const toggleExpand = (rowId, columnKey) => {
        const key = columnKey ? `${rowId}-${columnKey}` : rowId;
        setExpandedRows(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

  
    const showWarningAlert = (message) => {
        if (alertTimeoutRef.current) {
            clearTimeout(alertTimeoutRef.current);
        }
        
        if (!showSearchWarning) {
            setShowSearchWarning(true);
            toast.warning(message, {
                toastId: 'search-warning', 
                onClose: () => setShowSearchWarning(false)
            });
            
            alertTimeoutRef.current = setTimeout(() => {
                setShowSearchWarning(false);
            }, 3000);
        }
    };


    const parseVoiceCommand = (query) => {
        const payload = {
            TaskID: null,
            IssueActionorDecision: null,
            GroupID: null,
            TaskCoordinatorID: null,
            OfficerResponsible: null,
            priority: null,
            Status: null,
            Category: null,
            StartDate: null,
            EndDate: null,
            DeadlineStartDate: null,
            DeadlineEndDate: null,
            followedBy: null,
            Attachments: null,
        };

        const lowerText = query.toLowerCase();
        
        const taskIdMatch = lowerText.match(/task\s?id\s?(\d+)/i);
        if (taskIdMatch) payload.TaskID = taskIdMatch[1].trim();

  
        if (lowerText.includes('issue') || lowerText.includes('action')) {
            payload.IssueActionorDecision = lowerText
                .replace(/issue|action|decision/g, '')
                .trim();
        }

   
        const priorityMatch = lowerText.match(/priority\s?(low|medium|high|top)/i);
        if (priorityMatch) {
            const matchedPriority = prioritiesArray.find(
                p => p.priorityName?.toLowerCase() === priorityMatch[1]
            );
            if (matchedPriority) payload.priority = matchedPriority.priorityID;
        }
        const statusMatch = lowerText.match(/status\s?(pending|completed|in progress)/i);
        if (statusMatch) {
            const matchedStatus = statusArray.find(
                s => s.statusName?.toLowerCase() === statusMatch[1]
            );
            if (matchedStatus) payload.Status = matchedStatus.statusID;
        }

        if (lowerText.includes('category')) {
            const val = lowerText.replace(/category/, '').trim();
            const matchedCategory = categoriesArray.find(
                p => p.categoryName?.toLowerCase() === val
            );
            if (matchedCategory) payload.Category = matchedCategory.categoryID;
        }

        if (lowerText.includes('group')) {
            const val = lowerText.replace(/group/, '').trim();
            const matchedGroup = groupsArray.find(
                p => p.name?.toLowerCase() === val
            );
            if (matchedGroup) payload.GroupID = matchedGroup.groupID;
        }

        if (lowerText.includes('coordinator') || lowerText.includes('task coordinator')) {
            const val = lowerText.replace(/task coordinator|coordinator/g, '').trim();
            const matchedCoordinator = usersArray.find(user =>
                user.username.toLowerCase() === val);
            if (matchedCoordinator) payload.TaskCoordinatorID = matchedCoordinator.userID;
        }

        if (lowerText.includes('date')) {
            const val = lowerText.replace(/date/g, '').trim();
            try {
                const formatted = format(new Date(val), 'yyyy-MM-dd');
                payload.StartDate = formatted;
            } catch (error) {
                console.log('Invalid date format');
            }
        }

        if (lowerText.includes('deadline')) {
            const val = lowerText.replace(/deadline/g, '').trim();
            try {
                const formatted = format(new Date(val), 'yyyy-MM-dd');
                payload.DeadlineStartDate = formatted;
            } catch (error) {
                console.log('Invalid deadline format');
            }
        }

        if (lowerText.includes('attachment')) {
            payload.Attachments = true;
        }

        return payload;
    };

    const fetchTasks = async (query) => {
   
        setShowSearchWarning(false);
        if (alertTimeoutRef.current) {
            clearTimeout(alertTimeoutRef.current);
        }

        if (!query || query.trim() === '' || query === 'Listening...') {
            setFilteredTasks([]);
            setTasks([]);
            setApiError(null);
            return;
        }

        if (!hasSearchParameters(query)) {
            showWarningAlert("Please specify search parameters like Task ID, Priority, Status etc.");
            return;
        }

        setIsLoading(true);
        setApiError(null);

        try {
            const payload = parseVoiceCommand(query);
            const response = await apiPost('/auth/GetTaskDetail', payload);

            let data = [];
            if (Array.isArray(response)) {
                data = response;
            } else if (response?.data) {
                data = Array.isArray(response.data) ? response.data : [response.data];
            }

            const formattedTasks = data.map(task => ({
                ...task,
                taskId: task.taskID || task.taskId,
                issueActionOrDecision: task.issueActionOrDecision || 'N/A',
                priorityName: task.priorityName || 'N/A',
                dateOfDecisionOrInitiation: task.dateOfDecisionOrInitiation,
                deadline: task.deadline,
                coordinatorNames: task.coordinatorNames || 'N/A',
                officerNames: task.officerNames || 'N/A',
                categoryName: task.categoryName || 'N/A',
                actionTakenOrStatus: task.actionTakenOrStatus || 'N/A',
                additionalComments: task.additionalComments || 'N/A',
                taskAttachments: task.taskAttachments ? task.taskAttachments.split(',') : []
            }));

            setTasks(formattedTasks);
            setFilteredTasks(formattedTasks);

            if (data.length === 0) {
                toast.info("No tasks found matching your criteria", {
                    toastId: 'no-tasks-found'
                });
            }
        } catch (error) {
            console.error('API Error:', error);
            setApiError(error.message || 'Failed to fetch tasks. Please try again.');
            setTasks([]);
            setFilteredTasks([]);
            toast.error("Failed to search tasks", {
                toastId: 'api-error'
            });
        } finally {
            setIsLoading(false);
        }
    };

    const fetchInitialData = async () => {
        try {
            setIsLoading(true);
            const [
                usersRes,
                prioritiesRes,
                categoriesRes,
                groupsRes,
                statusesRes
            ] = await Promise.all([
                apiGet('/User/GetAllUsers'),
                apiGet('/User/GetAllPriority'),
                apiGet('/User/GetAllCategories'),
                apiGet('/Group/GetAll'),
                apiGet('/User/GetAllStatus')
            ]);

            if (usersRes?.isSuccess) {
                usersArray = usersRes.data || [];
                setAllUsers(usersArray);

                const coordinators = usersArray
                    .filter(user => user.roleName?.toLowerCase() === 'taskcoordinator')
                    .map(user => ({
                        value: user.userID,
                        label: user.username
                    }));
                setTaskCoordinators(coordinators);
            }

            if (prioritiesRes?.isSuccess) {
                prioritiesArray = prioritiesRes.data || [];
                setPriorities(prioritiesArray);
            }

            if (categoriesRes?.isSuccess) {
                categoriesArray = categoriesRes.data || [];
                setCategories(categoriesArray);
            }

            if (groupsRes?.isSuccess) {
                groupsArray = groupsRes.data || [];
                const formattedGroups = groupsArray.map(group => ({
                    value: group.groupID,
                    label: group.name
                }));
                setGroups(formattedGroups);
            }

            if (statusesRes?.isSuccess) {
                statusArray = statusesRes.data || [];
                setStatuses(statusArray);
            }
        } catch (error) {
            console.error('Error fetching initial data:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const startVoiceRecognition = () => {
        try {
            setVoiceQuery('Listening...');
            setIsListening(true);
            setApiError(null);
            setShowSearchWarning(false);
            if (alertTimeoutRef.current) {
                clearTimeout(alertTimeoutRef.current);
            }
            recognitionRef.current.start();
        } catch (error) {
            console.error('Voice recognition error:', error);
            setApiError('Microphone access denied. Please allow microphone permissions.');
            setIsListening(false);
        }
    };

    const stopVoiceRecognition = () => {
        if (recognitionRef.current) {
            recognitionRef.current.stop();
        }
        setIsListening(false);
    };

    const handleManualSearch = (e) => {
        const query = e.target.value;
        setVoiceQuery(query);
 
        setShowSearchWarning(false);
        if (alertTimeoutRef.current) {
            clearTimeout(alertTimeoutRef.current);
        }

        if (!query.trim() || query === 'Listening...') {
            setFilteredTasks([]);
            setTasks([]);
            setApiError(null);
            return;
        }
        
 
        if (alertTimeoutRef.current) {
            clearTimeout(alertTimeoutRef.current);
        }
        
        alertTimeoutRef.current = setTimeout(() => {
            if (!hasSearchParameters(query)) {
                showWarningAlert("Please specify search parameters like Task ID, Priority, Status etc.");
                return;
            }
            fetchTasks(query);
        }, 500); 
    };

   
    const handleShowDeadlineHistory = async (taskId) => {
        try {
            setDeadlineHistoryModal(prev => ({
                ...prev,
                visible: true,
                loading: true,
                currentTaskId: taskId,
            }));

            const response = await apiGet(`/TaskTracking/task-deadlines/${taskId}`);

            if (response?.isSuccess && response.data) {
                const sortedDeadlines = response.data.sort(
                    (a, b) => new Date(b.changeDate) - new Date(a.changeDate)
                );

                setDeadlineHistoryModal(prev => ({
                    ...prev,
                    deadlines: sortedDeadlines,
                    loading: false,
                }));
            } else {
                setDeadlineHistoryModal(prev => ({ ...prev, loading: false }));
                toast.error(response?.message || "No deadline history found");
            }
        } catch (error) {
            console.error("Error fetching deadline history:", error);
            setDeadlineHistoryModal(prev => ({ ...prev, loading: false }));
            toast.error("Failed to load deadline history");
        }
    };

    const handleShowAttachments = async (task) => {
        setAttachmentsModal({
            show: true,
            taskId: task.taskID,
            officerNames: task.officerNames || 'Officer',
            attachments: task.taskAttachments || [],
            onFileDelete: () => {}
        });
    };

    const handleShowOfficerComments = async (task) => {
        try {
            const endpointsToTry = [
                `/Task/GetTaskComments?taskId=${task.taskID}`,
                `/api/TaskComments/${task.taskID}`,
                `/TaskComments/GetByTaskId/${task.taskID}`
            ];

            let response;

            for (const endpoint of endpointsToTry) {
                try {
                    response = await apiGet(endpoint);
                    if (response) break;
                } catch (err) {
                    console.log(`Failed to fetch from ${endpoint}`, err);
                }
            }

            const comments = response?.data || [];

            setCommentsModal({
                show: true,
                taskId: task.taskID,
                officerNames: task.officerNames || 'Officer',
                comments: Array.isArray(comments) ? comments : [],
                existingComments: task.additionalComments || 'No comments available'
            });

        } catch (error) {
            console.error('Error loading comments:', error);
            toast.error('Could not load comments');

            setCommentsModal({
                show: true,
                taskId: task.taskID,
                officerNames: task.officerNames || 'Officer',
                comments: [],
                existingComments: task.additionalComments || 'No comments available'
            });
        }
    };

    const columns = [
        {
            name: 'Task ID',
            selector: row => row.taskID || row.taskId,
            sortable: true,
            width: '140px',
            center: true,
            style: {
                borderRight: '1px solid #dee2e6',
                textAlign: "center"
            },
        },
        {
            name: 'Issue, Action or Decision',
            cell: row => {
                const text = row.issueActionOrDecision || '';
                const isExpanded = expandedRows[`${row.taskID}-issue`];
                const shortText = text.length > 100 ? text.slice(0, 100) + '...' : text;

                return (
                    <div style={{ minWidth: '200px', whiteSpace: 'pre-wrap' }}>
                        {isExpanded ? text : shortText}
                        {text.length > 100 && (
                            <span 
                                onClick={() => toggleExpand(row.taskID, 'issue')} 
                                style={{ color: '#007bff', cursor: 'pointer', marginLeft: '5px' }}
                            >
                                {isExpanded ? 'View Less' : 'View More'}
                            </span>
                        )}
                    </div>
                );
            },
            wrap: true,
            minWidth: '240px',
            center: true,
            style: {
                borderRight: '1px solid #dee2e6',
                textAlign: "left"
            },
        },
        {
            name: 'Priority',
            cell: row => {
                const priority = row.priorityName || '';
                let color = "#1e293b";
                switch (priority.toLowerCase()) {
                    case "top": color = "#92400e"; break;
                    case "high": color = "#7f1d1d"; break;
                    case "medium": color = "#1e3a8a"; break;
                }
                return (
                    <span style={{
                        display: "inline-block", 
                        padding: "4px 12px", 
                        borderRadius: "999px",
                        color: color, 
                        fontWeight: "600", 
                        fontSize: "13px",
                        textTransform: "capitalize"
                    }}>
                        {priority}
                    </span>
                );
            },
            sortable: true,
            width: '120px',
            center: true,
            style: {
                borderRight: '1px solid #dee2e6',
                textAlign: "center"
            },
        },
        {
            name: 'Date of Decision',
            cell: row => formatDate(row.dateOfDecisionOrInitiation),
            sortable: true,
            width: '190px',
            center: true,
            style: {
                borderRight: '1px solid #dee2e6',
                textAlign: "center"
            },
        },
        {
            name: "Deadline",
            cell: (row) => {
                const hasDeadlineHistory = row.allDeadline !== null && row.allDeadline.length > 0;

                return (
                    <div className="d-flex align-items-center">
                        <span>{formatDate(row.deadline)}</span>
                        {hasDeadlineHistory && (
                            <BsClockHistory
                                className="ms-2 text-muted cursor-pointer"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleShowDeadlineHistory(row.taskID);
                                }}
                                title="View deadline history"
                                style={{ fontSize: '1.2rem' }}
                            />
                        )}
                    </div>
                );
            },
            sortable: true,
            width: '150px',
            center: true,
            style: {
                borderRight: '1px solid #dee2e6',
                textAlign: "center"
            },
        },
        {
            name: 'Task Coordinator',
            selector: row => row.coordinatorNames || 'N/A',
            sortable: true,
            width: '210px',
            center: true,
            style: {
                borderRight: '1px solid #dee2e6',
                textAlign: "center"
            },
        },
        {
            name: 'Action Taken / Status',
            cell: row => {
                const text = row.actionTakenOrStatus || 'N/A';
                const key = `${row.taskID}-status`;
                const isExpanded = expandedRows[key];
                const shortText = text.length > 100 ? text.slice(0, 100) + '...' : text;

                return (
                    <div style={{ minWidth: '200px', whiteSpace: 'pre-wrap' }}>
                        {isExpanded ? text : shortText}
                        {text.length > 100 && (
                            <span
                                onClick={() => toggleExpand(row.taskID, 'status')}
                                style={{ color: '#007bff', cursor: 'pointer', marginLeft: '5px' }}
                            >
                                {isExpanded ? 'View Less' : 'View More'}
                            </span>
                        )}
                    </div>
                );
            },
            wrap: true,
            minWidth: '230px',
            style: {
                borderRight: '1px solid #dee2e6',
                textAlign: "left"
            },
        },
        {
            name: 'Officer Responsible',
            cell: row => {
                const hasAttachments = row.taskAttachments && row.taskAttachments.length > 0;
                const hasComments = row.additionalComments && row.additionalComments !== 'N/A';

                return (
                    <div className="d-flex align-items-center">
                        <span>{row.officerNames || 'N/A'}</span>

                        {hasComments && (
                            <BsChatText
                                className="ms-2 text-muted cursor-pointer"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleShowOfficerComments(row);
                                }}
                                title="View comments"
                            />
                        )}

                        {hasAttachments && (
                            <BsPaperclip
                                className="ms-2 text-muted cursor-pointer"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleShowAttachments(row);
                                }}
                                title="View attachments"
                            />
                        )}
                    </div>
                );
            },
            wrap: true,
            minWidth: '200px',
            style: {
                borderRight: '1px solid #dee2e6',
                textAlign: "left"
            },
        },
        {
            name: 'Additional Comments',
            cell: row => {
                const comments = row.additionalComments || row.officerComments || '';
                const isExpanded = expandedRows[`${row.taskID}-comments`];
                const shortText = comments.length > 50 ? comments.slice(0, 50) + '...' : comments;
                return (
                    <div style={{ minWidth: '150px', whiteSpace: 'pre-wrap' }}>
                        {isExpanded ? comments : shortText}
                        {comments.length > 50 && (
                            <span 
                                onClick={() => toggleExpand(row.taskID, 'comments')} 
                                style={{ color: '#007bff', cursor: 'pointer', marginLeft: '5px' }}
                            >
                                {isExpanded ? 'View Less' : 'View More'}
                            </span>
                        )}
                    </div>
                );
            },
            wrap: true,
            minWidth: '220px',
            center: true,
            style: {
                textAlign: "left"
            },
        }
    ];

    useEffect(() => {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SpeechRecognition) {
            setApiError('Voice recognition not supported in your browser');
            return;
        }

        recognitionRef.current = new SpeechRecognition();
        recognitionRef.current.lang = 'en-US';
        recognitionRef.current.interimResults = false;
        recognitionRef.current.maxAlternatives = 1;

        recognitionRef.current.onstart = () => {
            setIsListening(true);
            setVoiceQuery('Listening...');
            setFilteredTasks([]);
            setApiError(null);
            setShowSearchWarning(false);
        };

        recognitionRef.current.onresult = (event) => {
            const transcript = event.results[0][0].transcript.trim();
            
            if (!transcript) {
                setVoiceQuery('');
                return;
            }
            
            setVoiceQuery(transcript);
            fetchTasks(transcript);
        };

        recognitionRef.current.onerror = (event) => {
            console.error('Voice recognition error:', event.error);
            setApiError(`Voice recognition error: ${event.error}`);
            setIsListening(false);
            setIsLoading(false);
        };

        recognitionRef.current.onend = () => {
            setIsListening(false);
            if (voiceQuery === 'Listening...') {
                setVoiceQuery('');
            }
        };

        fetchInitialData();
        return () => {
            if (recognitionRef.current) {
                recognitionRef.current.stop();
            }
            if (alertTimeoutRef.current) {
                clearTimeout(alertTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        return () => {
            if (alertTimeoutRef.current) {
                clearTimeout(alertTimeoutRef.current);
            }
        };
    }, []);

    return (
        <div className="container mt-4 mb-4">
         <div className="tabs-section-wrapper mb-2">
        <div className='row'>
 <div className="d-flex justify-content-lg-center d-block tabs-section gap-3 mb-2">
                <Link to="/dashboard/reports"><Button variant="light"  className="btn-tab  btn-btn-sechover">Task Detail</Button></Link>
                <Link to="/dashboard/UserLoginHistory"><Button variant="light"  className="btn-tab  btn-btn-sechover">User Login History</Button></Link>
                <Link to="/dashboard/UserTaskUpdate"><Button variant="light"  className="btn-tab  btn-btn-sechover">User Task Update</Button></Link>
                <Link to="/dashboard/UserFrequency"><Button variant="light"  className="btn-tab  btn-btn-sechover">User Frequency</Button></Link>
                <Link to="/dashboard/voiceSearch"><Button className="btn-btn-primary btn-tab">VoiceSearch</Button></Link>
            </div>
  </div>  </div>
            <div className='row custombox'>
                <div className='rounded p-4 mb-4'>
                    <div className="text-center customheader mb-3 pb-4">
                        <h5 className="mb-3">Voice Search</h5>
                        <div className="d-flex justify-content-center">
                            <div className="input-group voice-searchinput" style={{ maxWidth: '600px' }}>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Speak to search (e.g., 'Show tasks with high priority')"
                                    value={voiceQuery}
                                    onChange={handleManualSearch}
                                />
                                <button
                                    className={`btn ${isListening ? 'btn-danger' : 'btn-outline-primary'}`}
                                    type="button"
                                    onClick={isListening ? stopVoiceRecognition : startVoiceRecognition}
                                    title={isListening ? 'Stop Listening' : 'Start Voice Search'}
                                    disabled={isLoading}
                                >
                                    {isListening ? <BsMicMute /> : <BsMic />}
                                </button>
                            </div>
                        </div>
                        {apiError && (
                            <div className="d-flex justify-content-center">
                                <div
                                    className="alert alert-danger mt-3"
                                    role="alert"
                                    style={{ width: "600px" }}
                                >
                                    {apiError}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="card shadow-sm rounded-4 py-4 px-0 border-0 bg-white mb-custom">
                <div className="mb-3">
                    <span className="text-muted">
                        {filteredTasks.length} tasks found {voiceQuery && voiceQuery !== 'Listening...' && `for "${voiceQuery}"`}
                    </span>
                </div>
                <DataTable
                    columns={columns}
                    data={filteredTasks.map((task, idx) => ({
                        ...task,
                        _rowIndex: idx,
                    }))}
                    pagination
                    responsive
                    progressPending={isLoading}
                    progressComponent={
                        <div className="py-4 text-center">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-2">
                                {isListening ? 'Listening... Speak now' : 'Searching tasks...'}
                            </p>
                        </div>
                    }
                    noHeader={false}
                    fixedHeader
                    fixedHeaderScrollHeight="calc(100vh - 300px)"
                    className="p-datatable-striped customuser-data-table customarchive-data-table dashboard-table"
                    customStyles={{
                        table: {
                            style: {
                                fontWeight: '600',
                                fontSize: '14px',
                                backgroundColor: '#f8f9fa',
                                textAlign: 'center',
                            },
                        },
                        headRow: {
                            style: {
                                backgroundColor: "#04364A!important",
                                minHeight: "48px",
                                textAlign: 'center',
                            },
                        },
                        headCells: {
                            style: {
                                padding: "10px 14px",
                                fontSize: "15px",
                                fontWeight: "700",
                                color: "white",
                                textTransform: "uppercase",
                            },
                        },
                        cells: {
                            style: {
                                padding: "10px 14px",
                                fontSize: "14px",
                                color: "#334155",
                            },
                        },
                    }}
                    conditionalRowStyles={[
                        {
                            when: (row) => row._rowIndex % 2 === 0,
                            style: { backgroundColor: '#f2f2f2' },
                        },
                        {
                            when: (row) => row._rowIndex % 2 !== 0,
                            style: { backgroundColor: '#ffffff' },
                        },
                    ]}
                    noDataComponent={
                        <div className="py-4">
                            {voiceQuery === 'Listening...' ? 'Speak now to search tasks' :
                             voiceQuery ? `No tasks found for "${voiceQuery}"` : 
                             'No tasks available. Use voice search or type to find tasks'}
                        </div>
                    }
                />
            </div>

            <DeadlineHistoryModal
                show={deadlineHistoryModal.visible}
                onHide={() => setDeadlineHistoryModal(prev => ({ ...prev, visible: false }))}
                deadlines={deadlineHistoryModal.deadlines}
                loading={deadlineHistoryModal.loading}
                taskId={deadlineHistoryModal.currentTaskId}
            />

            <AttachmentsModal
                show={attachmentsModal.show}
                onHide={() => setAttachmentsModal(prev => ({ ...prev, show: false }))}
                taskId={attachmentsModal.taskId}
                officerNames={attachmentsModal.officerNames}
                onFileDelete={attachmentsModal.onFileDelete}
            />

            <CommentsModal
                show={commentsModal.show}
                onHide={() => setCommentsModal(prev => ({ ...prev, show: false }))}
                taskId={commentsModal.taskId}
                type={commentsModal.type}
                officerNames={commentsModal.officerNames}
                coordinatorName={commentsModal.coordinatorName}
            />
        </div>
    );
};

export default VoiceSearch;